import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ExpenseService } from '@/services/expense.service';
import {
  ExpenseStatus,
  ExpenseStatusLabels,
  ExpenseCategoryLabels,
} from '@/models/expense.model';
import { format } from 'date-fns';
import { Button, Card, Badge, LoadingSpinner, EmptyState, FormTextarea } from '@/components/common';
import { FileText, ArrowLeft, Edit, Trash2, CheckCircle2, XCircle, DollarSign, Calendar, Download, User, Tag } from 'lucide-react';

export const ExpenseReportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const { data: report, isLoading, error } = useQuery({
    queryKey: ['expenseReport', id],
    queryFn: () => (id ? ExpenseService.getReportById(id) : null),
    enabled: Boolean(id),
  });

  const approveMutation = useMutation({
    mutationFn: async () => {
      if (!id || !profile) return;
      await ExpenseService.updateReport(id, {
        status: ExpenseStatus.approved,
        approvedBy: profile.id,
        approvedByName: profile.fullName || profile.email || '',
        approvedAt: new Date(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenseReport', id] });
      queryClient.invalidateQueries({ queryKey: ['expenseReports'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async () => {
      if (!id || !profile) return;
      await ExpenseService.updateReport(id, {
        status: ExpenseStatus.rejected,
        rejectedBy: profile.id,
        rejectedAt: new Date(),
        rejectionReason,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenseReport', id] });
      queryClient.invalidateQueries({ queryKey: ['expenseReports'] });
      setShowRejectModal(false);
    },
  });

  const markAsPaidMutation = useMutation({
    mutationFn: async () => {
      if (!id || !profile) return;
      await ExpenseService.updateReport(id, {
        status: ExpenseStatus.paid,
        paidAt: new Date(),
        paidBy: profile.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenseReport', id] });
      queryClient.invalidateQueries({ queryKey: ['expenseReports'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) return;
      await ExpenseService.deleteReport(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenseReports'] });
      navigate('/expenses');
    },
  });

  const getStatusVariant = (status: ExpenseStatus): "success" | "warning" | "danger" | "gray" | "info" => {
    const variants: Record<string, "success" | "warning" | "danger" | "gray" | "info"> = {
      draft: 'gray',
      submitted: 'info',
      pending_approval: 'warning',
      approved: 'success',
      rejected: 'danger',
      pending_payment: 'info',
      paid: 'success',
      cancelled: 'gray',
    };
    return variants[status] || 'gray';
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading report..." fullScreen />;
  }

  if (error || !report) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          icon={FileText}
          title="Report not found"
          description="The requested expense report does not exist or you may not have access."
          action={
            <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/expenses')}>
              Back to Expenses
            </Button>
          }
        />
      </div>
    );
  }

  const canEdit = report.status === ExpenseStatus.draft || report.status === ExpenseStatus.rejected;
  const canApprove = report.status === ExpenseStatus.submitted || report.status === ExpenseStatus.pendingApproval;
  const canMarkAsPaid = report.status === ExpenseStatus.approved;
  const canDelete = report.status === ExpenseStatus.draft;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/expenses')}
        >
          Back to Expenses
        </Button>
      </div>

      {/* Header */}
      <Card className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">{report.title}</h1>
                <Badge variant={getStatusVariant(report.status)} size="md">
                  {ExpenseStatusLabels[report.status]}
                </Badge>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                <span>Report #{report.reportNumber}</span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {format(report.startDate, 'MMM d')} - {format(report.endDate, 'MMM d, yyyy')}
                </span>
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {report.employeeName}
                </span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold text-gray-900">
              ${report.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-sm text-gray-500">{report.expenseIds?.length || 0} expenses</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 pt-6 border-t border-gray-200 flex flex-wrap gap-3">
          {canEdit && (
            <Button
              variant="primary"
              size="sm"
              icon={Edit}
              onClick={() => navigate(`/expenses/reports/${id}/edit`)}
            >
              Edit Report
            </Button>
          )}
          {canApprove && (
            <>
              <Button
                variant="success"
                size="sm"
                icon={CheckCircle2}
                onClick={() => approveMutation.mutate()}
                isLoading={approveMutation.isPending}
              >
                Approve Report
              </Button>
              <Button
                variant="danger"
                size="sm"
                icon={XCircle}
                onClick={() => setShowRejectModal(true)}
              >
                Reject Report
              </Button>
            </>
          )}
          {canMarkAsPaid && (
            <Button
              variant="primary"
              size="sm"
              icon={DollarSign}
              onClick={() => markAsPaidMutation.mutate()}
              isLoading={markAsPaidMutation.isPending}
            >
              Mark as Paid
            </Button>
          )}
          {canDelete && (
            <Button
              variant="danger"
              size="sm"
              icon={Trash2}
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this report? Expenses will be unlinked but not deleted.')) {
                  deleteMutation.mutate();
                }
              }}
              isLoading={deleteMutation.isPending}
            >
              Delete Report
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            icon={Download}
            onClick={() => {
              if (report.expenses) {
                const csv = ExpenseService.exportExpensesToCSV(report.expenses);
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `expense-report-${report.reportNumber}.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
              }
            }}
          >
            Export CSV
          </Button>
        </div>

        {/* Rejection Reason */}
        {report.status === ExpenseStatus.rejected && report.rejectionReason && (
          <Card className="mt-6 bg-red-50 border-red-200">
            <h3 className="text-sm font-semibold text-red-800 mb-2">Rejection Reason</h3>
            <p className="text-sm text-red-700">{report.rejectionReason}</p>
            {report.rejectedAt && (
              <p className="text-xs text-red-600 mt-2">
                Rejected on {format(report.rejectedAt, 'MMMM d, yyyy')}
              </p>
            )}
          </Card>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Expenses List */}
        <div className="lg:col-span-2">
          <Card padding="none">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Expenses</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {report.expenses && report.expenses.length > 0 ? (
                report.expenses.map((expense) => (
                  <Link
                    key={expense.id}
                    to={`/expenses/${expense.id}`}
                    className="block px-6 py-4 hover:bg-gray-50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{expense.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {ExpenseCategoryLabels[expense.category]} • {format(expense.expenseDate, 'MMM d, yyyy')}
                          {expense.merchant && ` • ${expense.merchant}`}
                        </p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-sm font-medium text-gray-900">
                          ${expense.amount.toFixed(2)}
                        </p>
                        {expense.attachments.length > 0 || expense.receiptUrl ? (
                          <span className="text-xs text-green-600">Receipt attached</span>
                        ) : (
                          <span className="text-xs text-yellow-600">No receipt</span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="px-6 py-8 text-center text-gray-500">
                  No expenses in this report
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Summary Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Financial Summary */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              Financial Summary
            </h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Subtotal</dt>
                <dd className="text-sm font-medium text-gray-900">${report.subtotal.toFixed(2)}</dd>
              </div>
              {report.taxTotal > 0 && (
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Tax</dt>
                  <dd className="text-sm font-medium text-gray-900">${report.taxTotal.toFixed(2)}</dd>
                </div>
              )}
              <div className="flex justify-between pt-3 border-t">
                <dt className="text-base font-medium text-gray-900">Total</dt>
                <dd className="text-base font-bold text-gray-900">${report.total.toFixed(2)}</dd>
              </div>
              
              {report.reimbursableAmount > 0 && (
                <div className="flex justify-between pt-3 border-t">
                  <dt className="text-sm text-gray-500">Reimbursable</dt>
                  <dd className="text-sm font-medium text-green-600">${report.reimbursableAmount.toFixed(2)}</dd>
                </div>
              )}
              {report.nonReimbursableAmount > 0 && (
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Non-Reimbursable</dt>
                  <dd className="text-sm font-medium text-gray-600">${report.nonReimbursableAmount.toFixed(2)}</dd>
                </div>
              )}
              {report.billableAmount > 0 && (
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Billable to Client</dt>
                  <dd className="text-sm font-medium text-blue-600">${report.billableAmount.toFixed(2)}</dd>
                </div>
              )}
            </dl>
          </Card>

          {/* Category Breakdown */}
          {report.categoryBreakdown && Object.keys(report.categoryBreakdown).length > 0 && (
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <Tag className="w-5 h-5 text-blue-600" />
                By Category
              </h3>
              <div className="space-y-3">
                {Object.entries(report.categoryBreakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([category, amount]) => {
                    const percent = report.total > 0 ? (amount / report.total) * 100 : 0;
                    return (
                      <div key={category}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">{ExpenseCategoryLabels[category as keyof typeof ExpenseCategoryLabels] || category}</span>
                          <span className="font-medium">${amount.toFixed(2)}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </Card>
          )}

          {/* Report Details */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Report Details
            </h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Report Number</dt>
                <dd className="text-sm text-gray-900">{report.reportNumber}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Period</dt>
                <dd className="text-sm text-gray-900">
                  {format(report.startDate, 'MMM d, yyyy')} - {format(report.endDate, 'MMM d, yyyy')}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Employee</dt>
                <dd className="text-sm text-gray-900">{report.employeeName}</dd>
              </div>
              {report.employeeDepartment && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Department</dt>
                  <dd className="text-sm text-gray-900">{report.employeeDepartment}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="text-sm text-gray-900">{format(report.createdAt, 'MMM d, yyyy')}</dd>
              </div>
              {report.submittedAt && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Submitted</dt>
                  <dd className="text-sm text-gray-900">{format(report.submittedAt, 'MMM d, yyyy')}</dd>
                </div>
              )}
              {report.approvedAt && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Approved</dt>
                  <dd className="text-sm text-gray-900">
                    {format(report.approvedAt, 'MMM d, yyyy')}
                    {report.approvedByName && ` by ${report.approvedByName}`}
                  </dd>
                </div>
              )}
              {report.paidAt && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Paid</dt>
                  <dd className="text-sm text-gray-900">{format(report.paidAt, 'MMM d, yyyy')}</dd>
                </div>
              )}
            </dl>
          </Card>

          {/* Tags */}
          {report.tags && report.tags.length > 0 && (
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-blue-600" />
                Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {report.tags.map((tag) => (
                  <Badge key={tag} variant="info" size="sm">{tag}</Badge>
                ))}
              </div>
            </Card>
          )}

          {/* Notes */}
          {report.notes && (
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Notes
              </h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">{report.notes}</p>
            </Card>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowRejectModal(false)}></div>
            <Card className="relative max-w-md w-full">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Reject Report</h3>
              <p className="text-sm text-gray-500 mb-4">
                Please provide a reason for rejecting this expense report. The employee will be notified.
              </p>
              <FormTextarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                placeholder="Enter rejection reason..."
                error={!rejectionReason.trim() ? 'Rejection reason is required' : undefined}
              />
              <div className="mt-4 flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowRejectModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={() => rejectMutation.mutate()}
                  disabled={!rejectionReason.trim() || rejectMutation.isPending}
                  isLoading={rejectMutation.isPending}
                >
                  Reject Report
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};
